/**
 * What a person sees when a screen crashes.
 *
 * Without this, a render error is a blank white page: they see nothing, they
 * can tell you nothing, and you find out only if they bother to complain. So
 * this does three things — says something went wrong in plain words, offers the
 * one action that usually works, and shows a reference that leads you straight
 * back to the recording of what they were doing.
 */
import { RotateCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { captureClientError, currentSessionId } from '@/adapters/observability/posthog.client'
import { Button } from '@/components/ui/button'

interface ErrorScreenProps {
  error: unknown
  /** Router-provided: clears the error and re-renders the route. */
  reset?: () => void
}

export function ErrorScreen({ error, reset }: ErrorScreenProps) {
  const [reference, setReference] = useState<string | null>(null)

  useEffect(() => {
    captureClientError(error, { surface: 'error-boundary' })
    setReference(currentSessionId())
  }, [error])

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 p-6 text-center">
      <div className="max-w-measure space-y-2">
        <h2 className="font-semibold text-xl">Something went wrong on this screen</h2>
        <p className="text-muted-foreground text-sm">
          {reference
            ? 'The problem has been reported. Trying again usually works — if it keeps happening, send us the reference below.'
            : 'Trying again usually works. If it keeps happening, let us know what you were doing.'}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={() => (reset ? reset() : window.location.reload())}>
          <RotateCw aria-hidden="true" />
          Try again
        </Button>
      </div>

      {reference ? (
        <p className="text-muted-foreground text-xs">
          Reference <code className="font-mono">{reference}</code>
        </p>
      ) : null}
    </div>
  )
}
